import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Modal,
  Form,
  Button,
  Table,
} from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import { Styles, UniversityModalStyles } from "./styles/adminUniversities.js";
import { useAuth } from "../../context/AuthContext";
import {
  ADMIN_UNIVERSITIES_ENDPOINT,
  ADMIN_UNIVERSITIES_UPDATE_ENDPOINT,
  ADMIN_GALLERY_ENDPOINT,
  API_BASE_URL,
} from "../../config/api";

const initialForm = {
  universityName: "",
  universityImage: "",
  description: "",
  enabled: true,
};

const stripHtml = (value = "") =>
  value.replace(/<\/?[^>]+(>|$)/g, " ").replace(/\s+/g, " ").trim();

const normalizeBoolean = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "off"].includes(normalized)) {
      return false;
    }
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  return false;
};

const truncate = (value, maxLength = 140) => {
  if (!value) {
    return "—";
  }
  const trimmed = stripHtml(value);
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength - 3)}...`;
};

const AdminUniversities = () => {
  const { user, loading: authLoading } = useAuth();

  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [enabledFilter, setEnabledFilter] = useState("");
  const [modalState, setModalState] = useState({
    show: false,
    mode: "add",
    targetUniversity: null,
    form: initialForm,
    saving: false,
    error: "",
  });
  const [deletingId, setDeletingId] = useState("");
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState("");
  const [gallerySelection, setGallerySelection] = useState("");

  const isAdmin = useMemo(() => {
    if (!Array.isArray(user?.roles)) {
      return false;
    }
    return user.roles.some(
      (role) => typeof role === "string" && role.toLowerCase() === "admin"
    );
  }, [user]);

  const fetchUniversities = useCallback(async () => {
    if (!isAdmin) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (searchTerm.trim()) {
        params.set("search", searchTerm.trim());
      }
      if (enabledFilter) {
        params.set("enabled", enabledFilter);
      }

      const response = await fetch(
        `${ADMIN_UNIVERSITIES_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load universities.");
      }

      const payload = await response.json();
      const list = Array.isArray(payload.universities)
        ? payload.universities
        : [];

      setUniversities(list);

      if (payload.pagination) {
        const serverPage = payload.pagination.page || page;
        setPagination({
          page: serverPage,
          limit: payload.pagination.limit || pageSize,
          totalItems: payload.pagination.totalItems || 0,
          totalPages: payload.pagination.totalPages || 1,
        });
        if (serverPage !== page) {
          setPage(serverPage);
        }
      } else {
        setPagination({
          page,
          limit: pageSize,
          totalItems: list.length,
          totalPages: 1,
        });
      }
    } catch (err) {
      setError(err.message || "Unable to load universities.");
      setUniversities([]);
      setPagination((prev) => ({
        ...prev,
        totalItems: 0,
        totalPages: 1,
      }));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, pageSize, searchTerm, enabledFilter]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchUniversities();
    }
  }, [authLoading, isAdmin, fetchUniversities]);

  const tableRows = useMemo(
    () =>
      universities.map((university) => ({
        id: university._id || university.id,
        name: university.universityName || "Unnamed University",
        image: university.universityImage || "",
        description: university.description || "",
        enabled: normalizeBoolean(university.enabled),
        raw: university,
      })),
    [universities]
  );

  const loadGalleryImages = useCallback(async () => {
    if (!isAdmin) {
      return;
    }

    setGalleryLoading(true);
    setGalleryError("");

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(ADMIN_GALLERY_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load gallery images.");
      }

      const payload = await response.json();
      setGalleryImages(Array.isArray(payload.images) ? payload.images : []);
    } catch (err) {
      setGalleryImages([]);
      setGalleryError(err.message || "Unable to load gallery images.");
    } finally {
      setGalleryLoading(false);
    }
  }, [isAdmin]);

  const openModal = (mode, targetUniversity = null) => {
    if (mode === "edit" && targetUniversity) {
      setModalState({
        show: true,
        mode: "edit",
        targetUniversity,
        form: {
          universityName: targetUniversity.universityName || "",
          universityImage: targetUniversity.universityImage || "",
          description: targetUniversity.description || "",
          enabled: normalizeBoolean(targetUniversity.enabled ?? true),
        },
        saving: false,
        error: "",
      });
      setGallerySelection(targetUniversity.universityImage || "");
    } else {
      setModalState({
        show: true,
        mode: "add",
        targetUniversity: null,
        form: initialForm,
        saving: false,
        error: "",
      });
      setGallerySelection("");
    }
  };

  const closeModal = () => {
    setModalState({
      show: false,
      mode: "add",
      targetUniversity: null,
      form: initialForm,
      saving: false,
      error: "",
    });
    setGallerySelection("");
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: value,
      },
      error: "",
    }));
    if (name === "universityImage") {
      setGallerySelection("");
    }
  };

  const handleEnabledChange = (event) => {
    const { checked } = event.target;
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        enabled: checked,
      },
      error: "",
    }));
  };

  const handleDescriptionChange = (value) => {
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        description: value,
      },
      error: "",
    }));
  };

  const handleGallerySelect = (event) => {
    const selected = event.target.value;
    setGallerySelection(selected);
    if (!selected) {
      return;
    }
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        universityImage: selected,
      },
      error: "",
    }));
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setSuccess("");
    setSearchTerm(searchInput);
  };

  const handleSearchClear = () => {
    if (!searchInput && !searchTerm) {
      return;
    }
    setSearchInput("");
    setSearchTerm("");
    setPage(1);
    setSuccess("");
  };

  const handleEnabledFilterChange = (event) => {
    setEnabledFilter(event.target.value);
    setPage(1);
    setSuccess("");
  };

  const handlePageSizeChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setPageSize(Number.isFinite(value) ? value : 10);
    setPage(1);
  };

  const handlePrevPage = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setPage((prev) => prev + 1);
  };

  useEffect(() => {
    if (!modalState.show) {
      return;
    }
    loadGalleryImages();
  }, [modalState.show, loadGalleryImages]);

  useEffect(() => {
    if (!modalState.show) {
      return;
    }

    const currentImage = modalState.form.universityImage;
    if (!currentImage) {
      setGallerySelection("");
      return;
    }

    const match = galleryImages.some((image) => {
      const absolute =
        image.url && image.url.startsWith("http")
          ? image.url
          : `${API_BASE_URL}${image.url || ""}`;
      return absolute === currentImage;
    });

    setGallerySelection(match ? currentImage : "");
  }, [modalState.show, modalState.form.universityImage, galleryImages]);

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "clean"],
      ],
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "align",
      "link",
    ],
    []
  );

  const submitModal = async (event) => {
    event.preventDefault();
    const mode = modalState.mode;
    const targetId =
      modalState.targetUniversity?._id || modalState.targetUniversity?.id || "";

    setModalState((prev) => ({
      ...prev,
      saving: true,
      error: "",
    }));
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const payload = {
        universityName: modalState.form.universityName.trim(),
        universityImage: modalState.form.universityImage.trim(),
        description: modalState.form.description,
        enabled: modalState.form.enabled,
      };

      const endpoint =
        mode === "edit"
          ? ADMIN_UNIVERSITIES_UPDATE_ENDPOINT(targetId)
          : ADMIN_UNIVERSITIES_ENDPOINT;
      const response = await fetch(endpoint, {
        method: mode === "edit" ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result.message ||
            `Unable to ${mode === "edit" ? "update" : "create"} university.`
        );
      }

      closeModal();
      await fetchUniversities();
      setSuccess(
        result.message ||
          (mode === "edit"
            ? "University updated successfully."
            : "University created successfully.")
      );
    } catch (err) {
      setModalState((prev) => ({
        ...prev,
        saving: false,
        error:
          err.message ||
          `Unable to ${modalState.mode === "edit" ? "update" : "create"} university.`,
      }));
    }
  };

  const handleDelete = async (targetUniversity) => {
    if (!targetUniversity) {
      return;
    }

    const id = targetUniversity._id || targetUniversity.id;
    if (!id) {
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this university?"
    );
    if (!confirmed) {
      return;
    }

    setDeletingId(id);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(ADMIN_UNIVERSITIES_UPDATE_ENDPOINT(id), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || "Unable to delete university.");
      }

      await fetchUniversities();
      setSuccess(result.message || "University deleted successfully.");
    } catch (err) {
      setError(err.message || "Unable to delete university.");
    } finally {
      setDeletingId("");
    }
  };

  const canGoNext =
    pagination.page < pagination.totalPages && pagination.totalPages > 1;
  const canGoPrev = pagination.page > 1;

  return (
    <Styles>
      <UniversityModalStyles />
      <div className="main-wrapper admin-universities-page">
        <HeaderTwo />
        <BreadcrumbBox title="Admin Universities" />

        <section className="universities-area">
          <Container>
            <Row>
              <Col md="12">
                {!authLoading && !isAdmin && (
                  <Alert variant="danger">
                    You do not have permission to view this page.
                  </Alert>
                )}

                {isAdmin && (
                  <div className="card-box">
                    {error && (
                      <Alert variant="danger" className="mb-4">
                        {error}
                      </Alert>
                    )}
                    {success && (
                      <Alert variant="success" className="mb-4">
                        {success}
                      </Alert>
                    )}

                    <div className="actions">
                      <div className="filters">
                        <Form onSubmit={handleSearchSubmit}>
                          <Form.Control
                            type="text"
                            placeholder="Search universities"
                            value={searchInput}
                            onChange={(event) =>
                              setSearchInput(event.target.value)
                            }
                          />
                          <Button type="submit" variant="primary">
                            Search
                          </Button>
                          <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={handleSearchClear}
                          >
                            Clear
                          </Button>
                        </Form>
                        <Form.Control
                          as="select"
                          value={enabledFilter}
                          onChange={handleEnabledFilterChange}
                        >
                          <option value="">All statuses</option>
                          <option value="true">Enabled</option>
                          <option value="false">Disabled</option>
                        </Form.Control>
                      </div>

                      <div className="action-buttons">
                        <Button type="button" onClick={() => openModal("add")}>
                          Add University
                        </Button>
                      </div>
                    </div>

                    <div className="data-grid">
                      {loading ? (
                        <div className="text-center my-5">
                          <Spinner animation="border" role="status" />
                        </div>
                      ) : (
                        <>
                          <Table responsive bordered hover>
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Image</th>
                                <th>Description</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tableRows.length === 0 ? (
                                <tr>
                                  <td colSpan="5" className="text-center">
                                    No universities found.
                                  </td>
                                </tr>
                              ) : (
                                tableRows.map((row) => (
                                  <tr key={row.id}>
                                    <td>{row.name}</td>
                                    <td>
                                      {row.image ? (
                                        <img
                                          src={row.image}
                                          alt={row.name}
                                          className="thumbnail"
                                        />
                                      ) : (
                                        "—"
                                      )}
                                    </td>
                                    <td className="description-cell">
                                      {truncate(row.description)}
                                    </td>
                                    <td>{row.enabled ? "Enabled" : "Disabled"}</td>
                                    <td className="actions-cell">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => openModal("edit", row.raw)}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        disabled={deletingId === row.id}
                                        onClick={() => handleDelete(row.raw)}
                                      >
                                        {deletingId === row.id
                                          ? "Deleting..."
                                          : "Delete"}
                                      </Button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>

                          <div className="pagination-controls">
                            <div className="page-info">
                              Page {pagination.page} of {pagination.totalPages} •{" "}
                              {pagination.totalItems} total
                            </div>
                            <div className="page-actions">
                              <Button
                                type="button"
                                onClick={handlePrevPage}
                                disabled={!canGoPrev}
                              >
                                Previous
                              </Button>
                              <Button
                                type="button"
                                onClick={handleNextPage}
                                disabled={!canGoNext}
                              >
                                Next
                              </Button>
                              <Form.Control
                                as="select"
                                value={pageSize}
                                onChange={handlePageSizeChange}
                              >
                                <option value={5}>5 / page</option>
                                <option value={10}>10 / page</option>
                                <option value={20}>20 / page</option>
                                <option value={50}>50 / page</option>
                              </Form.Control>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>

      <Modal
        show={modalState.show}
        onHide={closeModal}
        centered
        className="admin-universities-modal"
      >
        <Form onSubmit={submitModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalState.mode === "edit" ? "Edit University" : "Add University"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalState.error && (
              <Alert variant="danger" className="mb-3">
                {modalState.error}
              </Alert>
            )}

            <Form.Group controlId="universityName">
              <Form.Label>University Name</Form.Label>
              <Form.Control
                type="text"
                name="universityName"
                value={modalState.form.universityName}
                onChange={handleFormChange}
                placeholder="Enter university name"
                required
              />
            </Form.Group>

            <Form.Group controlId="universityImage">
              <Form.Label>University Image</Form.Label>
              <Form.Control
                type="url"
                name="universityImage"
                value={modalState.form.universityImage}
                onChange={handleFormChange}
                placeholder="https://example.com/image.jpg"
                disabled={modalState.saving}
              />
              <div className="gallery-picker">
                <span className="muted">
                  Paste an external URL or choose an uploaded gallery image.
                </span>
                {galleryLoading ? (
                  <div className="gallery-status">
                    <Spinner animation="border" size="sm" role="status" />
                    <span>Loading gallery…</span>
                  </div>
                ) : (
                  <>
                    <Form.Control
                      as="select"
                      value={gallerySelection}
                      onChange={handleGallerySelect}
                      disabled={
                        modalState.saving || galleryImages.length === 0
                      }
                    >
                      <option value="">Select from gallery…</option>
                      {galleryImages.map((image) => {
                        const absolute =
                          image.url && image.url.startsWith("http")
                            ? image.url
                            : `${API_BASE_URL}${image.url || ""}`;
                        return (
                          <option key={image.fileName} value={absolute}>
                            {image.fileName}
                          </option>
                        );
                      })}
                    </Form.Control>
                    {galleryImages.length === 0 && !galleryError && (
                      <span className="muted">
                        No gallery images available yet.
                      </span>
                    )}
                  </>
                )}
                {galleryError && (
                  <span className="text-danger small">{galleryError}</span>
                )}
              </div>
              {modalState.form.universityImage && (
                <div className="image-preview">
                  <span className="preview-label">Preview</span>
                  <img
                    src={modalState.form.universityImage}
                    alt="University visual preview"
                    className="img-fluid"
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group controlId="universityDescription">
              <Form.Label>Description</Form.Label>
              <ReactQuill
                theme="snow"
                value={modalState.form.description}
                onChange={handleDescriptionChange}
                modules={quillModules}
                formats={quillFormats}
                readOnly={modalState.saving}
              />
              <Form.Text muted>
                Use the toolbar to format university details displayed to users.
              </Form.Text>
            </Form.Group>

            <Form.Group controlId="universityEnabled">
              <Form.Check
                type="switch"
                label="Enabled"
                checked={modalState.form.enabled}
                onChange={handleEnabledChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeModal}
              disabled={modalState.saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={modalState.saving}>
              {modalState.saving
                ? "Saving..."
                : modalState.mode === "edit"
                ? "Update University"
                : "Create University"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Styles>
  );
};

export default AdminUniversities;
